import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useStudyPlan } from "../../../contexts/study.context";
import API from "../../../utils/api";
import { Modal, SpecialTextField } from "../..";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import CircularProgress from "@material-ui/core/CircularProgress";
import ToolTip from "../../tooltip";
import PrimaryButton from "../../buttons/primary";
import OutlinedButton from "../../buttons/outlined";
import { getSitesParameter } from "../../../../study.plans/study.plan/utils/helpers";
import { useActivatedSitesLiveTotal } from "../../../hooks/useActivatedSitesLiveTotal";
import { ActivatedSitesStats } from "../../../../study.plans/study.plan/activated.sites.stats";

const timeframes = [
  { value: "Super Short", key: "SuperShort", label: "Super Short" },
  { value: "Short", key: "Short", label: "Short" },
  { value: "Medium", key: "Medium", label: "Medium" },
  { value: "Long", key: "Long", label: "Long" },
];

const siteIdData = [
  {
    value: "Small",
    label: "Small",
    days: "timeSiteIdSmallDays",
    sites: "1-2 sites",
  },
  {
    value: "Medium",
    label: "Medium",
    days: "timeSiteIdMediumDays",
    sites: "3-10 sites",
  },
  {
    value: "Large",
    label: "Large",
    days: "timeSiteIdLargeDays",
    sites: "10+ sites",
  },
];

const EditCountryModal = ({
  countryId,
  siteGroup,
  currentEffort,
  currentTimeframe,
  studyPlanType,
  open = false,
  onClose,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [manualEffort, setManualEffort] = useState(currentEffort);
  const {
    saveLoading,
    setIsCountryUpdate,
    isCountryUpdate,
    studyPlan,
    siteActivationStats,
  } = useStudyPlan();
  const { parameters } = siteGroup;

  const initialSitesToActivate = getSitesParameter(parameters);
  const [sitesToActivate, setSitesToActivate] = useState(initialSitesToActivate);
  const [currentCountryStartUp, setCurrentCountryStartUp] = useState(currentTimeframe);

  useEffect(() => {

    API.getSiteGroups()
      .then((res) => {
        const index = res.data.map((site) => site.countryId).indexOf(countryId);
        setSelectedCountry(res.data[index]);
      })
      .catch((err) => console.error(err));
  }, []);

  const isSiteNumberEmpty = () => {
    return (
      !sitesToActivate.lowerBound ||
      (studyPlanType === 1 && !sitesToActivate.upperBound)
    );
  };

  const handleSave = () => {
    const body = {
      studyPlanId: studyPlan.studyPlanId,
      siteGroupId: siteGroup.siteGroupId,
      NUM_SITES_ACTIVATED_LOWER: sitesToActivate.lowerBound,
      NUM_SITES_ACTIVATED_UPPER: sitesToActivate.upperBound,
      siteIdEffort: manualEffort,
      countryStartUpTimeFrame: currentCountryStartUp,
    };

    setIsCountryUpdate(false);
    API.postSiteGroup(body, studyPlan.studyId)
      .then((res) => {
        setIsCountryUpdate(false);
        onClose();
        // Force a page refresh after successful save
        window.location.reload(true);
      })
      .catch((error) => {
        console.error("Error saving plan:", error);
        // Handle error appropriately
        setIsCountryUpdate(true);
      });
  };

  const handleCountryStartUpChange = (value) => {
    setCurrentCountryStartUp(value);
    setIsCountryUpdate(true);
  };

  const handleCancel = () => {
    onClose();
  };

  const getEffortFromSites = (value) => {
    const numericValue = Number(value);
    if (numericValue >= 1 && numericValue <= 2) {
      return "Small";
    } else if (numericValue >= 3 && numericValue <= 10) {
      return "Medium";
    } else if (numericValue > 10) {
      return "Large";
    }
    return null;
  };

  const handleActivatedSitesParameter = (param, property, value) => {
    const boundToUse = studyPlanType === 1 ? "upperBound" : "lowerBound";
    
    // Build the next state object first
    const nextSitesToActivate = { ...sitesToActivate, [property]: value };
    
    // Update state with it
    setSitesToActivate(nextSitesToActivate);
    
    // Use the next state for calculations to avoid stale state issues
    const siteValue =
      property === boundToUse
        ? value
        : studyPlanType === 1
        ? nextSitesToActivate.upperBound
        : nextSitesToActivate.lowerBound;

    if (siteValue === "" || siteValue === null) {
      setManualEffort(null);
    } else {
      const newEffort = getEffortFromSites(siteValue);
      if (newEffort) {
        setManualEffort(newEffort);
      }
    }
    // Enable the save button by setting isCountryUpdate to true
    setIsCountryUpdate(true);
  };

  const liveTotalForActivatedSites = useActivatedSitesLiveTotal(
    studyPlan.studyPlanType,
    initialSitesToActivate.lowerBound,
    sitesToActivate.lowerBound,
    initialSitesToActivate.upperBound,
    sitesToActivate.upperBound,
    siteActivationStats.sitesActivatedLowerBound,
    siteActivationStats.sitesActivatedUpperBound,
    siteActivationStats.totalSitesRequired
  );

  const handleManualEffortChange = (event) => {
    const newEffort = event.target.value;
    setManualEffort(newEffort);
    setIsCountryUpdate(true);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit country"
      aria-describedby="edit a country"
      title="Edit Country"
      size={620}
    >
      <Container>
        <Typography>
          The below should be confirmed by the appropriate functional area.
        </Typography>
        <FormControl variant="outlined">
          <div style={{ display: "flex-wrap" }}>
            <div style={{ display: "flex", gap: "1em", alignItems: "center" }}>
              <SpecialTextField
                altlabel={studyPlanType !== 1 ? "" : "Lower Bound"}
                value={sitesToActivate.lowerBound}
                onChange={(value) => {
                  handleActivatedSitesParameter(
                    sitesToActivate,
                    "lowerBound",
                    value,
                  );
                }}
                key={sitesToActivate.parameterName + 1}
                showArrows={false}
              />
              {studyPlanType === 1 && (
                <SpecialTextField
                  altlabel="Upper Bound"
                  value={sitesToActivate.upperBound}
                  onChange={(value) =>
                    handleActivatedSitesParameter(
                      sitesToActivate,
                      "upperBound",
                      value,
                    )
                  }
                  key={sitesToActivate.parameterName + 2}
                  showArrows={false}
                />
              )}
              <p>Number of Activated Sites</p>
              <ToolTip
                zIndex={1500}
                info="Complete the range of sites targeted for each country; ensure the total number of sites across all countries will allow for scenarios that match the total number of sites entered in Overall Parameters; the modeling will return the quickest scenario to last participant enrolled."
              />
            </div>
            <div style={{ marginTop: "1em" }}>
              <ActivatedSitesStats overrides={liveTotalForActivatedSites} studyPlanType={studyPlan.studyPlanType} />
            </div>
          </div>
          
        </FormControl>
        <FormControl variant="outlined">
          <InputLabel id="site-id-label">Site ID Effort</InputLabel>
          <Select
            labelId="site-id-label"
            id="select-site-id"
            value={manualEffort || ""}
            onChange={handleManualEffortChange}
            label="Site ID Effort"
          >
            {siteIdData.map(({ value, label, days, sites }) => (
              <MenuItem
                key={value}
                value={value}
              >
                {label}{" "}
                {selectedCountry && `(${selectedCountry[days]}; ${sites})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined">
          <InputLabel id="country-startup-label">
            Select Country Start-Up Time Frame
          </InputLabel>
          <Select
            labelId="country-startup-label"
            id="select-country-startup"
            value={currentCountryStartUp}
            onChange={(event) =>
              handleCountryStartUpChange(event.target.value)
            }
            label="Select Country Start-Up Time Frame"
          >
            {timeframes.map(({ value, key, label }) => (
              <MenuItem
                key={key}
                value={value}
              >
                {label}{" "}
                {selectedCountry &&
                  `(${selectedCountry[`timeCountryStartUp${key}Days`]}; ${(
                    selectedCountry[`timeCountryStartUp${key}Days`] / 30.42
                  ).toFixed(2)} months)`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <ButtonContainer>
          <OutlinedButton
            onClick={handleCancel}
            disabled={saveLoading}
          >
            Cancel
          </OutlinedButton>
          <PrimaryButton
            onClick={handleSave}
            disabled={saveLoading || !isCountryUpdate || isSiteNumberEmpty()}
          >
            {saveLoading ? (
              <CircularProgress
                style={{ color: "var(--White)" }}
                size="1em"
              />
            ) : (
              "Save"
            )}
          </PrimaryButton>
        </ButtonContainer>
      </Container>
    </Modal>
  );
};

export default EditCountryModal;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1em;
`;

const Container = styled.div`
  display: grid;
  grid-gap: 2em;
`;
